import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * "Removed from my store" — the merchant-level block list.
 *
 * Backed by merchant_config.business_rules.skip_list. This is NOT the same thing as
 * unchecking a product for a supplier (useProviderSelection): that only stops sourcing it
 * from THAT supplier and leaves the product on the storefront if another supplier has it.
 * Removing takes the product off the store for every supplier, stops it being re-created
 * by the sync, and zeroes its Shopify stock.
 *
 * Writes go through the merchant-remove-products edge function rather than a direct table
 * update, because the Processors page saves the whole config_object from its own state —
 * writing from here would risk resetting processor fields this page never loaded.
 */
export const useRemovedProducts = (merchantId: string | null) => {
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!merchantId) {
      setRemoved(new Set());
      setLoaded(false);
      return;
    }
    try {
      setError(null);
      const { data: merchant, error: mErr } = await supabase
        .from('merchants')
        .select('user_config_id')
        .eq('id', merchantId)
        .maybeSingle();
      if (mErr) throw mErr;
      if (!merchant?.user_config_id) {
        setRemoved(new Set());
        setLoaded(true);
        return;
      }

      const { data: config, error: cErr } = await supabase
        .from('merchant_config')
        .select('config_object')
        .eq('id', merchant.user_config_id)
        .maybeSingle();
      if (cErr) throw cErr;

      const list = (config?.config_object as any)?.business_rules?.skip_list ?? [];
      // Entries may be bare strings or {sku, title} objects — the workers accept both.
      const skus = (Array.isArray(list) ? list : [])
        .map((item: any) => (typeof item === 'string' ? item : item?.sku))
        .filter((s: any): s is string => typeof s === 'string' && s.trim() !== '');
      setRemoved(new Set(skus));
      setLoaded(true);
    } catch (e: any) {
      console.error('Failed to load removed products:', e);
      setError(e?.message || 'Failed to load removed products');
      setLoaded(true);
    }
  }, [merchantId]);

  useEffect(() => {
    load();
  }, [load]);

  const mutate = useCallback(
    async (add: string[], remove: string[]) => {
      if (!merchantId) return false;
      setSaving(true);
      setError(null);
      try {
        const { data, error: fnErr } = await supabase.functions.invoke('merchant-remove-products', {
          body: { shop_id: merchantId, add, remove },
        });
        if (fnErr) throw fnErr;
        if (data?.error) throw new Error(data.error);

        // Reflect the server's view rather than guessing locally.
        setRemoved((prev) => {
          const next = new Set(prev);
          add.forEach((s) => next.add(s));
          remove.forEach((s) => next.delete(s));
          return next;
        });
        return true;
      } catch (e: any) {
        console.error('Failed to update removed products:', e);
        setError(e?.message || 'Failed to update removed products');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [merchantId],
  );

  const removeProduct = useCallback((sku: string) => mutate([sku], []), [mutate]);
  const restoreProduct = useCallback((sku: string) => mutate([], [sku]), [mutate]);
  const isRemoved = useCallback((sku: string) => removed.has(sku), [removed]);

  return {
    removed,
    removedCount: removed.size,
    loaded,
    saving,
    error,
    isRemoved,
    removeProduct,
    restoreProduct,
    refresh: load,
  };
};
